import { c as _c } from "react/compiler-runtime";
import { createNestablePublicClientApplication } from '@azure/msal-browser';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { Client } from '@microsoft/microsoft-graph-client';
import { useEffect, useRef, useState } from 'react';
import { MsalAuthenticationProvider } from '../auth/msal-authentication-provider';
import { createMsalConfig, MSAL_SCOPES } from '../config/msal-config';
import { createGraphStorage } from '../storage/graph-storage';
import { AuthOptions, type AuthState } from '../types';

/**
 * MS Graph API hook with zero configuration needed.
 * All config (tenant, client ID, redirect URI, scopes) is auto-detected from environment.
 */
export function useGraphAPI() {
  const $ = _c(23);
  const applicationRef = useRef(null);
  const authenticationProviderRef = useRef(null);
  const [graphClient, setGraphClient] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = createGraphStorage();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const storage = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => storage.current.loadAuthState();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [authState, setAuthState] = useState(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = async () => {
      if (applicationRef.current) {
        return;
      }
      const msalConfig = createMsalConfig();
      applicationRef.current = await createNestablePublicClientApplication(msalConfig);
      authenticationProviderRef.current = new MsalAuthenticationProvider(applicationRef.current, MSAL_SCOPES, msalConfig.auth.redirectUri || "");
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const initOnce = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (state, setComponentState) => {
      storage.current.saveAuthState(state);
      if (setComponentState) {
        setAuthState(state);
      }
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const setGraphOAuthState = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setGraphClient(Client.initWithMiddleware({
        authProvider: authenticationProviderRef.current
      }));
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const createGraphClient = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = async () => {
      await initOnce();
      setGraphOAuthState({
        state: AuthOptions.INTERACTION_IN_PROGRESS,
        accessToken: null
      }, false);
      const state_0 = await authenticationProviderRef.current.login();
      setGraphOAuthState(state_0, true);
      if (state_0.state === AuthOptions.LOGGED_IN) {
        createGraphClient();
      } else {
        setGraphClient(null);
      }
      return state_0;
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const login = t5;
  let t6;
  if ($[6] !== authState.state) {
    t6 = () => authState.state === AuthOptions.LOGGED_OUT || authState.state === AuthOptions.INTERACTION_REQUIRED;
    $[6] = authState.state;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const shouldTriggerOfficeLogin = t6;
  let t7;
  if ($[8] !== authState.state) {
    t7 = () => authState.state === AuthOptions.INTERACTION_IN_PROGRESS;
    $[8] = authState.state;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const isOfficeLoginInProgress = t7;
  let t8;
  if ($[10] !== authState.state) {
    t8 = () => {
      if (authenticationProviderRef.current && authState.state === AuthOptions.LOGGED_IN) {
        return authenticationProviderRef.current.getAccessToken();
      }
      return null;
    };
    $[10] = authState.state;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const getAccessToken = t8;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = async () => {
      await initOnce();
      const accounts = applicationRef.current?.getAllAccounts();
      if (accounts && accounts.length > 0) {
        await applicationRef.current?.logoutPopup({
          account: accounts[0]
        });
      }
      storage.current.clearAuthState();
      setAuthState({
        state: AuthOptions.LOGGED_OUT
      });
      setGraphClient(null);
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const logout = t9;
  let t10;
  let t11;
  if ($[13] !== authState.state || $[14] !== graphClient) {
    t10 = () => {
      let isMounted;
      isMounted = true;
      if (authState.state === AuthOptions.LOGGED_IN && !graphClient) {
        const restoreLoggedInState = async () => {
          await initOnce();
          if (isMounted) {
            createGraphClient();
          }
        };
        restoreLoggedInState();
      }
      return () => {
        isMounted = false;
      };
    };
    t11 = [authState.state, graphClient];
    $[13] = authState.state;
    $[14] = graphClient;
    $[15] = t10;
    $[16] = t11;
  } else {
    t10 = $[15];
    t11 = $[16];
  }
  useEffect(t10, t11);
  let t12;
  if ($[17] !== authState || $[18] !== getAccessToken || $[19] !== graphClient || $[20] !== isOfficeLoginInProgress || $[21] !== shouldTriggerOfficeLogin) {
    t12 = {
      authState,
      login,
      logout,
      graphClient,
      shouldTriggerOfficeLogin,
      isOfficeLoginInProgress,
      getAccessToken
    };
    $[17] = authState;
    $[18] = getAccessToken;
    $[19] = graphClient;
    $[20] = isOfficeLoginInProgress;
    $[21] = shouldTriggerOfficeLogin;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
}